import { useDispatch } from "react-redux";
import { TableSkeleton } from "../../../../components/Skeletons/TableSkeleton";
import { Accordion, Badge, Box, Table, Text } from "@mantine/core";
import IconChevronDown from "@tabler/icons-react/dist/esm/icons/IconChevronDown.mjs";
import {
  setPageMultipleClientReport,
  setSizeMultipleClientReport,
  useSelectEnabledMultipleClientReport,
  useSelectPageMultipleClientReport,
  useSelectReportsMultipleClientReport,
  useSelectSizeMultipleClientReport,
  useSelectTotalCountMultipleClientReport,
} from "../../../../redux/features/reports/multipleClientReportSlice";
import { AppPagination } from "../../../../components/AppPagination/AppPagination";
import { useTranslation } from "react-i18next";
import { useIsFetching } from "@tanstack/react-query";
import classes from "./multipleClientReportTable.module.scss";
import { EmptyState } from "../../../../components/EmptyState/EmptyState";

function FlagsTable({ flags, t }) {
  return (
    <Box className={classes["inner-table-wrapper"]}>
      <Table
        horizontalSpacing="md"
        verticalSpacing="sm"
        highlightOnHover
        className={classes["inner-table"]}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("reports.typeOfRule")}</Table.Th>
            <Table.Th>{t("reports.table.ruleName")}</Table.Th>
            <Table.Th>{t("reports.table.caseStatus")}</Table.Th>
            <Table.Th>{t("reports.resolutionStatus")}</Table.Th>
            <Table.Th>{t("reports.table.amlRisk")}</Table.Th>
            <Table.Th>{t("reports.table.amount")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {flags?.map((flag, idx) => (
            <Table.Tr key={`${flag.amlRisk}-${idx}`}>
              <Table.Td>{flag.typeOfRule}</Table.Td>
              <Table.Td>{flag.ruleName}</Table.Td>
              <Table.Td>{flag.caseStatus}</Table.Td>
              <Table.Td>{flag.resolutionStatus}</Table.Td>
              <Table.Td>{flag.amlRisk}</Table.Td>
              <Table.Td>{flag.amount}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
}

const MultipleClientReportTable = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const data = useSelectReportsMultipleClientReport();
  const page = useSelectPageMultipleClientReport();
  const size = useSelectSizeMultipleClientReport();
  const totalCount = useSelectTotalCountMultipleClientReport();
  const enabled = useSelectEnabledMultipleClientReport();

  const handlePageChange = (newPage) => {
    dispatch(setPageMultipleClientReport(newPage - 1));
  };

  const handleSizeChange = (newSize) => {
    dispatch(setSizeMultipleClientReport(newSize));
    dispatch(setPageMultipleClientReport(0));
  };

  const isFetching = useIsFetching({
    queryKey: ["get-multiple-client-report"],
  });

  if (!enabled) {
    return null;
  }

  return (
    <div className={classes["multiple-report-container"]}>
      <Box p="md">
        {isFetching ? (
          <Table>
            <TableSkeleton cols={1} length={size} />
          </Table>
        ) : data.length === 0 ? (
          <EmptyState />
        ) : (
          <Accordion
            variant="separated"
            chevronPosition="right"
            multiple
            radius="md"
            transitionDuration={250}
            chevron={
              <IconChevronDown
                size={18}
                className={classes["accordion-chevron"]}
              />
            }
            classNames={{
              root: classes["accordion-root"],
              item: classes["accordion-item"],
              control: classes["accordion-control"],
            }}
          >
            {data.map((row) => {
              const flagCount = row.flags?.length ?? 0;
              return (
                <Accordion.Item key={row.uic} value={String(row.uic)}>
                  <Accordion.Control>
                    <Box className={classes["uic-row"]}>
                      <Text className={classes["uic-prefix"]}>UIC</Text>
                      <Text className={classes["uic-value"]}>{row.uic}</Text>
                      <Box style={{ flex: 1 }} />
                      <Badge
                        size="md"
                        radius="sm"
                        variant="light"
                        color="secondary.9"
                        className={classes["flag-badge"]}
                      >
                        {flagCount} {flagCount === 1 ? "flag" : "flags"}
                      </Badge>
                    </Box>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <FlagsTable flags={row.flags} t={t} />
                  </Accordion.Panel>
                </Accordion.Item>
              );
            })}
          </Accordion>
        )}
      </Box>
      {totalCount > 0 && (
        <div className={classes["pagination-sticky-multiple"]}>
          <AppPagination
            page={page}
            take={size}
            total={totalCount}
            onPageChange={handlePageChange}
            onTakeChange={handleSizeChange}
          />
        </div>
      )}
    </div>
  );
};

export default MultipleClientReportTable;
